import React, { useEffect } from "react";
import { UIButton } from "@src/components/UIButton";
import UIModal from "@src/components/UIModal/UIModal";
import { useNotification } from "@src/context/NotificationContext";
import {
  useDeleteFeelingTag,
  useDeleteGenreTag,
} from "@src/queries/tagQueries";
import { useAppDispatch, useAppSelector } from "@src/redux/hooks";
import {
  resetDeleteTagState,
  setDeleteTagState,
  setRefetchTagsByTagType,
} from "@src/redux/slices/tagFactorySlice";
import { RootState } from "@src/redux/store";
import { IPlaylistRecord } from "@src/types/playlist";
import { ITagType } from "@src/types/tags";
import Spinner from "@src/components/Spinner";
import { theme } from "@src/theme";

interface DeleteTagModalProps {
  closeModal: () => void;
  isOpen: boolean;
  openModal: () => void;
  toggleModal: () => void;
  onConfirm: (data: any) => void;
  data?: IPlaylistRecord;
  tagType?: ITagType;
}

const DeleteTagModal: React.FC<DeleteTagModalProps> = ({
  closeModal,
  isOpen,
}) => {
  const { loading, tagData, tagType } = useAppSelector(
    (state: RootState) => state.tagFactory.deleteTagState
  );
  const dispatch = useAppDispatch();
  const tagMutationMap = {
    genre: useDeleteGenreTag,
    feeling: useDeleteFeelingTag,
  };

  const deleteTagMutation =
    (tagType && tagMutationMap[tagType]?.()) || useDeleteGenreTag();

  const { addNotification } = useNotification();
  const onSubmit = React.useCallback(() => {
    if (!tagType) return alert("Please provide a tag type");
    dispatch(
      setDeleteTagState({
        error: "",
        success: false,
        loading: true,
      })
    );
    deleteTagMutation.mutateAsync(
      {
        tagId: tagData.documentId,
      },
      {
        onSuccess: () => {
          addNotification("Success deleting tag!", "success");
          dispatch(setRefetchTagsByTagType(tagType));
          dispatch(resetDeleteTagState());
        },
        onError: () => {
          addNotification("Error deleting tag!", "error");
          dispatch(
            setDeleteTagState({
              error: "Error deleting tag",
              success: false,
              loading: false,
            })
          );
        },
      }
    );
  }, [tagType, tagData, deleteTagMutation, addNotification, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetDeleteTagState());
    };
  }, []);
  return (
    <UIModal
      actions={[
        <UIButton key="cancel" onClick={closeModal} variant="outline">
          Cancel
        </UIButton>,
        <UIButton
          type="submit"
          key="delete"
          onClick={onSubmit}
          variant="danger"
        >
          Delete
        </UIButton>,
      ]}
      isOpen={isOpen}
      title={`Delete ${tagData.title} tag`}
      onClose={closeModal}
    >
      <div
        style={{
          width: 300,
        }}
      >
        <p>Are you sure you want to delete this tag?</p>
        <p style={{ color: theme.colors.red, fontWeight: "bold" }}>
          This action cannot be undone.
        </p>
        <div
          style={{
            height: 20,
          }}
        >
          {loading && <Spinner small />}
        </div>
      </div>
    </UIModal>
  );
};

export default DeleteTagModal;
